/**
 * 行为埋点 SDK - 传输层
 * 队列管理、批量发送、背压保护
 * 注意：埋点不做 session 内去重，每次操作都记录
 */
import type { AnalyticsConfig, AnalyticsEvent } from './types';

let config: AnalyticsConfig | null = null;
let flushTimer: ReturnType<typeof setInterval> | null = null;

/** 服务端限流时暂停发送到期时间戳（ms），0 表示未限流 */
let pauseUntil = 0;

/** 内存队列 */
const queue: AnalyticsEvent[] = [];

/** 将事件推入队列（含背压保护和采样） */
export function enqueue(event: AnalyticsEvent): void {
  if (!config?.enabled) return;

  // sampleRate 采样
  const sampleRate = config.sampleRate ?? 1.0;
  if (sampleRate < 1.0 && Math.random() > sampleRate) return;

  // 内存队列背压：满了丢弃最旧的
  const maxSize = config.maxQueueSize ?? 200;
  if (queue.length >= maxSize) {
    queue.shift();
  }
  queue.push(event);

  // 达到 batchSize 时提前 flush（限流暂停中不主动触发）
  const batchSize = config.flushBatchSize ?? 10;
  if (queue.length >= batchSize && Date.now() >= pauseUntil) {
    void flush();
  }
}

/** 发送一批事件，支持 429 退避 */
async function sendBatch(batch: AnalyticsEvent[]): Promise<void> {
  if (!config?.endpoint || batch.length === 0) return;

  // 限流暂停期间：将事件推回队列头，等待恢复
  if (Date.now() < pauseUntil) {
    queue.unshift(...batch);
    return;
  }

  const payload = JSON.stringify({ events: batch });

  // 优先 sendBeacon（无法感知响应状态码，但可靠、不阻塞页面关闭）
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    const blob = new Blob([payload], { type: 'application/json' });
    const sent = navigator.sendBeacon(config.endpoint, blob);
    if (sent) return;
  }

  // fetch 降级：可读取 429 状态码实现退避
  try {
    const res = await fetch(config.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    });
    if (res.status === 429) {
      const retryAfter = Number(res.headers.get('Retry-After') ?? 60);
      pauseUntil = Date.now() + retryAfter * 1000;
      // 退回队列头，等待限流解除后重试（不丢数据）
      queue.unshift(...batch);
    }
  } catch {
    // 网络错误静默失败，不影响主业务
  }
}

/** 冲刷当前队列 */
export async function flush(): Promise<void> {
  if (queue.length === 0) return;
  // 限流暂停期间跳过，等下次定时触发时再检查
  if (Date.now() < pauseUntil) return;
  const batchSize = config?.flushBatchSize ?? 10;
  const batch = queue.splice(0, batchSize);
  await sendBatch(batch);
}

/** 初始化传输层 */
export function initTransport(cfg: AnalyticsConfig): void {
  config = cfg;

  if (flushTimer) clearInterval(flushTimer);
  const interval = cfg.flushInterval ?? 5000;
  flushTimer = setInterval(() => {
    void flush();
  }, interval);

  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => void flush(), { once: false });
    window.addEventListener('beforeunload', () => void flush(), { once: false });
  }
}

/** 销毁传输层（测试用） */
export function destroyTransport(): void {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  queue.length = 0;
  config = null;
}
